import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, Eye } from "lucide-react"
import { mlSheetService } from "../../services/mlSheetService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import axios from "../../config/axios"
import {
  PageLayout,
  Button,
  DataTable,
  DetailCard,
  FormField,
  StatusBadge,
} from "../common/index.js"

const EditMLSheet = () => {
  const { mlSheetId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [searchingShipments, setSearchingShipments] = useState(false)
  const [agents, setAgents] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [locations, setLocations] = useState([])
  const [mlSheet, setMLSheet] = useState(null)
  const [filteredShipments, setFilteredShipments] = useState([])
  const [selectedShipmentIds, setSelectedShipmentIds] = useState([])
  const [allShipments, setAllShipments] = useState([])
  const [loadedSelectedShipmentData, setLoadedSelectedShipmentData] = useState(false)
  const [selectAllChecked, setSelectAllChecked] = useState(false)

  const [formData, setFormData] = useState({
    from_location_ids: [],
    to_location_ids: [],
    from_date: "",
    to_date: "",
    vehicle_id: "",
    driver_contact: "",
    access_user_ids: [],
  })

  useEffect(() => {
    fetchMLSheet()
  }, [mlSheetId])

  useEffect(() => {
    searchShipments()
  }, [formData.from_location_ids, formData.to_location_ids, formData.from_date, formData.to_date])

  const fetchSelectedShipmentsData = async (shipmentIds) => {
    if (!shipmentIds || shipmentIds.length === 0) {
      setLoadedSelectedShipmentData(true)
      return
    }

    try {
      const shipmentDetailPromises = shipmentIds.map((id) =>
        axios
          .get(`/shipments/${id}`)
          .then((res) => res.data.shipment || res.data.data || res.data)
          .catch((err) => {
            console.error(`Failed to fetch shipment ${id}:`, err)
            return { shipment_id: id, id: id, isMinimal: true }
          })
      )

      const shipmentDetails = await Promise.all(shipmentDetailPromises)
      const validShipments = shipmentDetails.filter(Boolean)

      setAllShipments(validShipments)
      setFilteredShipments([])
      setLoadedSelectedShipmentData(true)
    } catch (error) {
      console.error("Error fetching selected shipments data:", error)
      setLoadedSelectedShipmentData(true)
    }
  }

  const fetchMLSheet = async () => {
    try {
      setLoading(true)
      const [mlRes, agentsRes, vehiclesRes, locationsRes] = await Promise.all([
        mlSheetService.getMLSheet(mlSheetId),
        mlSheetService.getAgents(),
        mlSheetService.getAllVehiclesNoPage(),
        mlSheetService.getLocations(),
      ])

      const mlData = mlRes.data.ml_sheet || mlRes.data.data
      setMLSheet(mlData)

      const agentsData = agentsRes.data.data || agentsRes.data.users || agentsRes.data || []
      const vehiclesData = vehiclesRes.data.data || vehiclesRes.data.vehicles || vehiclesRes.data || []
      const locationsData = locationsRes.data.hubs || locationsRes.data.data || locationsRes.data || []

      setAgents(Array.isArray(agentsData) ? agentsData : [])
      setVehicles(Array.isArray(vehiclesData) ? vehiclesData : [])
      setLocations(Array.isArray(locationsData) ? locationsData : [])

      const shipmentIds = mlData.shipment_ids || []
      setSelectedShipmentIds(shipmentIds)

      fetchSelectedShipmentsData(shipmentIds)

      const fromLocationIds = mlData.from_location_ids || []
      const toLocationIds = mlData.to_location_ids || []

      setFormData({
        from_location_ids: fromLocationIds,
        to_location_ids: toLocationIds,
        from_date: "",
        to_date: "",
        vehicle_id: mlData.vehicle_id,
        driver_contact: mlData.driver_contact,
        access_user_ids: mlData.access_users?.map((u) => u.user_id || u.id || u) || [],
      })
    } catch (error) {
      console.error("Error fetching ML Sheet:", error)
      Alert.error("Failed to load ML Sheet")
      navigate("/ml-sheets")
    } finally {
      setLoading(false)
    }
  }

  const searchShipments = async () => {
    if (
      formData.from_location_ids.length === 0 &&
      formData.to_location_ids.length === 0 &&
      !formData.from_date &&
      !formData.to_date
    ) {
      setFilteredShipments([])
      setSelectedShipmentIds([])
      setSelectAllChecked(false)
      return
    }

    try {
      setSearchingShipments(true)

      const searchParams = {}

      if (formData.from_location_ids.length > 0) {
        const fromLocations = formData.from_location_ids
          .map((id) => {
            const loc = locations.find((l) => (l.id || l.hub_id) === id)
            return loc ? loc.location || loc.hub_name || loc.name : null
          })
          .filter(Boolean)
        if (fromLocations.length > 0) {
          searchParams.from_location = fromLocations.join(",")
        }
      }

      if (formData.to_location_ids.length > 0) {
        const toLocations = formData.to_location_ids
          .map((id) => {
            const loc = locations.find((l) => (l.id || l.hub_id) === id)
            return loc ? loc.location || loc.hub_name || loc.name : null
          })
          .filter(Boolean)
        if (toLocations.length > 0) {
          searchParams.to_location = toLocations.join(",")
        }
      }

      if (formData.from_date) searchParams.from_date = formData.from_date
      if (formData.to_date) searchParams.to_date = formData.to_date
      if (formData.from_date) searchParams.from_date = formData.from_date
      if (formData.to_date) searchParams.to_date = formData.to_date

      const response = await mlSheetService.getBookedShipments(searchParams)
      const shipmentsData = response.data.shipments || response.data.data || response.data || []

      const newShipments = Array.isArray(shipmentsData)
        ? shipmentsData.filter((s) => !selectedShipmentIds.includes(s.shipment_id || s.id))
        : []

      setFilteredShipments(newShipments)
      setSelectAllChecked(false)
    } catch (error) {
      console.error("Error searching shipments:", error)
      setFilteredShipments([])
      setSelectAllChecked(false)
    } finally {
      setSearchingShipments(false)
    }
  }

  const handleLocationToggle = (locationType, locationId) => {
    setFormData((prev) => {
      const key = locationType === "from" ? "from_location_ids" : "to_location_ids"
      const currentIds = prev[key]

      if (currentIds.includes(locationId)) {
        return { ...prev, [key]: currentIds.filter((id) => id !== locationId) }
      } else {
        return { ...prev, [key]: [...currentIds, locationId] }
      }
    })
  }

  const handleDateChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleSelectShipment = (shipmentId) => {
    setSelectedShipmentIds((prev) =>
      prev.includes(shipmentId) ? prev.filter((id) => id !== shipmentId) : [...prev, shipmentId]
    )
  }

  const handleSelectAll = () => {
    if (selectAllChecked) {
      setSelectedShipmentIds([])
    } else {
      const allIds = new Set()

      selectedShipmentIds.forEach((id) => allIds.add(id))

      filteredShipments.forEach((s) => {
        const id = s.shipment_id || s.id
        allIds.add(id)
      })

      allShipments.forEach((s) => {
        const id = s.shipment_id || s.id
        allIds.add(id)
      })

      setSelectedShipmentIds(Array.from(allIds))
    }
    setSelectAllChecked(!selectAllChecked)
  }

  const handleVehicleChange = (e) => {
    const { value } = e.target
    const selectedVehicle = vehicles.find(
      (v) => v.id === Number.parseInt(value) || v.vehicle_id === value
    )
    setFormData({
      ...formData,
      vehicle_id: selectedVehicle?.id || selectedVehicle?.vehicle_id || value,
      driver_contact: selectedVehicle?.driver_contact || "",
    })
  }

  const handleDriverContactChange = (e) => {
    setFormData({
      ...formData,
      driver_contact: e.target.value,
    })
  }

  const handleAccessUserChange = (userId) => {
    setFormData((prev) => ({
      ...prev,
      access_user_ids: prev.access_user_ids.includes(userId)
        ? prev.access_user_ids.filter((id) => id !== userId)
        : [...prev.access_user_ids, userId],
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (formData.from_location_ids.length === 0) {
      Alert.error("Please select at least one from location")
      return
    }

    if (formData.to_location_ids.length === 0) {
      Alert.error("Please select at least one to location")
      return
    }

    if (!formData.vehicle_id) {
      Alert.error("Please select a vehicle")
      return
    }

    if (selectedShipmentIds.length === 0) {
      Alert.error("Please select at least one shipment")
      return
    }

    if (formData.access_user_ids.length === 0) {
      Alert.error("Please select at least one access user")
      return
    }

    try {
      setSubmitting(true)

      const payload = {
        from_location_ids: formData.from_location_ids,
        to_location_ids: formData.to_location_ids,
        vehicle_id: formData.vehicle_id,
        driver_contact: formData.driver_contact || "N/A",
        shipment_ids: selectedShipmentIds,
        access_user_ids: formData.access_user_ids,
      }

      await mlSheetService.updateMLSheet(mlSheetId, payload)
      Alert.success("ML Sheet updated successfully")
      navigate("/ml-sheets")
    } catch (error) {
      console.error("Error updating ML Sheet:", error)
      Alert.error(error.response?.data?.error || "Failed to update ML Sheet")
    } finally {
      setSubmitting(false)
    }
  }

  const getShipmentStatusVariant = (status) => {
    const statusMap = {
      booked: "info",
      dispatched: "warning",
      delivered: "success",
      cancelled: "danger",
    }
    return statusMap[status?.toLowerCase()] || "default"
  }

  const getCombinedShipments = () => {
    const shipmentMap = new Map()

    allShipments.forEach((s) => {
      const id = s.shipment_id || s.id
      if (id) {
        shipmentMap.set(id, { ...s, isFromMLSheet: true })
      }
    })

    filteredShipments.forEach((s) => {
      const id = s.shipment_id || s.id
      if (id && !shipmentMap.has(id)) {
        shipmentMap.set(id, { ...s, isFromMLSheet: false })
      }
    })

    return Array.from(shipmentMap.values())
  }

  if (loading || !loadedSelectedShipmentData) {
    return (
      <PageLayout title="Edit ML Sheet" maxWidth="max-w-7xl">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600">Loading ML Sheet...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  const combinedShipments = getCombinedShipments()
  const hasShipments = selectedShipmentIds.length > 0 || combinedShipments.length > 0

  const shipmentColumns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          checked={selectAllChecked}
          onChange={handleSelectAll}
          className="w-4 h-4"
        />
      ),
      width: "48px",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) => {
        const shipmentId = shipment.shipment_id || shipment.id
        return (
          <input
            type="checkbox"
            checked={selectedShipmentIds.includes(shipmentId)}
            onChange={() => handleSelectShipment(shipmentId)}
            className="w-4 h-4"
            onClick={(e) => e.stopPropagation()}
          />
        )
      },
    },
    {
      key: "shipment_id",
      label: "Shipment ID",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) => (
        <span className="font-regular text-secondary text-sm">
          {shipment.shipment_id || shipment.id}
        </span>
      ),
    },
    {
      key: "sender",
      label: "Sender",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) =>
        !shipment.isMinimal ? (
          <>
            <div className="font-regular text-secondary text-sm">{shipment.sender_name}</div>
            <div className="text-sm text-gray-600">{shipment.sender_contact}</div>
          </>
        ) : null,
    },
    {
      key: "receiver",
      label: "Receiver",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) =>
        !shipment.isMinimal ? (
          <>
            <div className="font-regular text-secondary text-sm">{shipment.receiver_name}</div>
            <div className="text-sm text-gray-600">{shipment.receiver_contact}</div>
          </>
        ) : null,
    },
    {
      key: "route",
      label: "Route",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) =>
        !shipment.isMinimal ? (
          <div className="font-regular text-secondary whitespace-nowrap">
            {shipment.sender_location || "N/A"}{" "}
            <span className="mx-1 text-gray-500">→</span>{" "}
            {shipment.receiver_location || "N/A"}
          </div>
        ) : null,
    },
    {
      key: "packages",
      label: "Packages",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) =>
        !shipment.isMinimal ? (
          <span className="inline-block px-3 py-1 text-secondaryfont-semibold text-sm">
            {shipment.no_of_articles}
          </span>
        ) : null,
    },
    {
      key: "amount",
      label: "Amount",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) =>
        !shipment.isMinimal ? `₹${shipment.total_amount?.toFixed(2)}` : null,
    },
    {
      key: "status",
      label: "Status",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) =>
        !shipment.isMinimal ? (
          <StatusBadge
            status={shipment.status}
            variant={getShipmentStatusVariant(shipment.status)}
          />
        ) : null,
    },
  ]

  const getShipmentActions = (shipment) => {
    const shipmentId = shipment.shipment_id || shipment.id
    if (shipment.isMinimal) return []
    return [
      {
        label: "View",
        icon: Eye,
        onClick: () => navigate(`/shipments/view/${shipmentId}`),
      },
    ]
  }

  const shipmentMobileCardRender = (shipment) => {
    const shipmentId = shipment.shipment_id || shipment.id
    const isSelected = selectedShipmentIds.includes(shipmentId)

    return (
      <div className={isSelected ? "bg-green-50 border border-green-300 rounded-sm p-2 -m-2" : ""}>
        <div className="flex items-start gap-3 mb-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handleSelectShipment(shipmentId)}
            className="w-5 h-5 mt-1"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="flex-1">
            <div className="font-bold text-blue-600 text-lg">{shipmentId}</div>
            {!shipment.isMinimal && (
              <StatusBadge
                status={shipment.status}
                variant={getShipmentStatusVariant(shipment.status)}
                className="mt-1"
              />
            )}
          </div>
        </div>

        {!shipment.isMinimal && (
          <>
            <div className="space-y-2 mb-3">
              <div className="bg-gray-50 rounded p-2">
                <div className="text-xs text-gray-600 font-semibold">Sender</div>
                <div className="font-semibold text-gray-800">{shipment.sender_name}</div>
                <div className="text-sm text-gray-600">{shipment.sender_contact}</div>
                <div className="text-xs text-gray-500">{shipment.sender_location || "N/A"}</div>
              </div>

              <div className="text-center text-gray-400">↓</div>

              <div className="bg-gray-50 rounded p-2">
                <div className="text-xs text-gray-600 font-semibold">Receiver</div>
                <div className="font-semibold text-gray-800">{shipment.receiver_name}</div>
                <div className="text-sm text-gray-600">{shipment.receiver_contact}</div>
                <div className="text-xs text-gray-500">{shipment.receiver_location || "N/A"}</div>
              </div>
            </div>

            <div className="flex justify-between items-center pt-3 border-t border-gray-200">
              <div className="flex gap-3">
                <div>
                  <div className="text-xs text-gray-600">Packages</div>
                  <div className="font-bold text-gray-800">{shipment.no_of_articles}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Amount</div>
                  <div className="font-bold text-gray-800">
                    ₹{shipment.total_amount?.toFixed(2)}
                  </div>
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/shipments/view/${shipmentId}`)
                }}
              >
                View
              </Button>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <PageLayout
      title={`Edit ML Sheet # ${mlSheet?.ml_sheet_id}`}
      maxWidth="max-w-7xl"
      actions={
        <>
          <Button onClick={handleSubmit} loading={submitting}>
            Update
          </Button>
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate("/ml-sheets")}>
            Back
          </Button>
        </>
      }
    >
      <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
          <DetailCard title="Location & Date Filters">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
              <FormField label="From Locations" required>
                <div className="border border-gray-300 rounded-sm p-3 max-h-48 overflow-y-auto bg-white">
                  {Array.isArray(locations) && locations.length > 0 ? (
                    <div className="space-y-2">
                      {locations.map((loc) => {
                        const locId = loc.id || loc.hub_id
                        return (
                          <label
                            key={locId}
                            className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.from_location_ids.includes(locId)}
                              onChange={() => handleLocationToggle("from", locId)}
                              className="w-4 h-4 flex-shrink-0"
                            />
                            <span className="ml-3 text-sm font-regular text-secondary">
                              {loc.location || loc.hub_name || loc.name}
                              <span className="text-xs text-gray-500 ml-2">
                                ({loc.destination_type})
                              </span>
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No locations available</p>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Selected: {formData.from_location_ids.length}
                </p>
              </FormField>

              <FormField label="To Locations" required>
                <div className="border border-gray-300 rounded-sm p-3 max-h-48 overflow-y-auto bg-white">
                  {Array.isArray(locations) && locations.length > 0 ? (
                    <div className="space-y-2">
                      {locations.map((loc) => {
                        const locId = loc.id || loc.hub_id
                        return (
                          <label
                            key={locId}
                            className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.to_location_ids.includes(locId)}
                              onChange={() => handleLocationToggle("to", locId)}
                              className="w-4 h-4 flex-shrink-0"
                            />
                            <span className="ml-3 text-sm font-regular text-secondary">
                              {loc.location || loc.hub_name || loc.name}
                              <span className="text-xs text-gray-500 ml-2">
                                ({loc.destination_type})
                              </span>
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No locations available</p>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Selected: {formData.to_location_ids.length}
                </p>
              </FormField>

              <FormField label="From Date">
                <input
                  type="date"
                  name="from_date"
                  value={formData.from_date}
                  onChange={handleDateChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm text-secondary"
                />
              </FormField>

              <FormField label="To Date">
                <input
                  type="date"
                  name="to_date"
                  value={formData.to_date}
                  onChange={handleDateChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm text-secondary"
                />
              </FormField>
            </div>
          </DetailCard>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
            <DetailCard title="Vehicle & Driver Information">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Vehicle" required>
                  <select
                    value={formData.vehicle_id}
                    onChange={handleVehicleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm text-secondary"
                  >
                    <option value="">Select a vehicle</option>
                    {vehicles.map((v) => (
                      <option key={v.id || v.vehicle_id} value={v.id || v.vehicle_id}>
                        {v.vehicle_number} - {v.vehicle_type || ""}
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField label="Driver Contact">
                  <input
                    type="text"
                    value={formData.driver_contact}
                    onChange={handleDriverContactChange}
                    placeholder="Enter driver contact number"
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm text-secondary"
                  />
                </FormField>
              </div>
            </DetailCard>

            <DetailCard title="Select Access Users">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {agents.length > 0 ? (
                  agents.map((agent) => (
                    <label
                      key={agent.user_id || agent.id}
                      className="flex items-center p-3 py-2 border border-gray-200 rounded-sm hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.access_user_ids.includes(agent.user_id || agent.id)}
                        onChange={() => handleAccessUserChange(agent.user_id || agent.id)}
                        className="w-4 h-4 flex-shrink-0"
                      />
                      <span className="ml-3 font-regular text-secondary text-sm">{agent.name}</span>
                    </label>
                  ))
                ) : (
                  <p className="text-gray-500 col-span-3">No agents available</p>
                )}
              </div>
            </DetailCard>
          </div>
        </div>

        <DetailCard title={`Select Shipments (${selectedShipmentIds.length})`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            {hasShipments && (
              <Button type="button" onClick={handleSelectAll}>
                {selectAllChecked ? "Deselect All" : "Select All"}
              </Button>
            )}
            {searchingShipments && (
              <p className="text-sm text-brand">Searching shipments...</p>
            )}
          </div>

          {searchingShipments ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
              <p className="text-gray-500 mt-4">Searching for shipments...</p>
            </div>
          ) : !hasShipments ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {formData.from_location_ids.length > 0 ||
                formData.to_location_ids.length > 0 ||
                formData.from_date ||
                formData.to_date
                  ? "No booked shipments found for selected filters"
                  : "Please select location or date filters to search for shipments"}
              </p>
            </div>
          ) : (
            <DataTable
              columns={shipmentColumns}
              data={combinedShipments}
              rowKey="shipment_id"
              actions={getShipmentActions}
              mobileCardRender={shipmentMobileCardRender}
              emptyMessage="No shipments found"
            />
          )}
        </DetailCard>
      </form>
    </PageLayout>
  )
}

export default EditMLSheet
